import * as jsxRuntime from "react/jsx-runtime";
import { Button, Dialog } from "@ngg/components";
import { XIcon } from "@ngg/icons";
import { cn as cn$1 } from "@ngg/storefront-utils";
import { extendTailwindMerge } from "tailwind-merge";
import cx from "classnames";
import { forwardRef, useState, useEffect } from "react";
import * as Radix from "@radix-ui/react-slider";
import * as Tooltip from "@radix-ui/react-tooltip";
const index = "";
const Fragment = jsxRuntime.Fragment;
const jsx = jsxRuntime.jsx;
const jsxs = jsxRuntime.jsxs;
const CloseButton = (onClose, className) => /* @__PURE__ */ jsx(Button, { type: "button", onClick: onClose, className: cn$1("border-0 bg-transparent p-0 text-black", className), children: /* @__PURE__ */ jsx(XIcon, { className: "text-base" }) });
const customTwMerge = extendTailwindMerge({
  classGroups: {
    text: ["text-xxs"]
  }
});
const cn = (...inputs) => customTwMerge(cx(...inputs));
function Cart(props) {
  const { open, hideCart, isEmpty, dictionary, classNames, children, EmptyCart, CartFooter } = props;
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      dialogTransitionProps: {
        enter: "ease-out duration-200",
        enterFrom: "translate-x-full",
        enterTo: "-translate-x-0",
        leave: "ease-in duration-200",
        leaveFrom: "-translate-x-0",
        leaveTo: "translate-x-full"
      },
      title: dictionary == null ? void 0 : dictionary.yourCart,
      open,
      onClose: hideCart,
      CloseButton: () => CloseButton(hideCart, "px-2 py-4 m-0"),
      classNames: {
        panel: cn([
          "py-0 rounded-none scrollbar-none fixed m-0 top-0 pb-0 right-0 z-40 flex bottom-0 flex-col overflow-y-auto overflow-x-hidden bg-white w-full max-w-[480px]",
          classNames == null ? void 0 : classNames.panel
        ]),
        title: "font-bold text-lg leading-4 tracking-0.02em",
        panelHeader: "flex w-full items-center justify-between px-3 pr-0 text-sm font-semibold"
      },
      children: [
        /* @__PURE__ */ jsx("hr", { className: "border-grey-150" }),
        isEmpty ? EmptyCart : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("div", { className: "scrollbar-none relative max-h-full basis-full overflow-y-auto", children }),
          CartFooter
        ] })
      ]
    }
  );
}
const localizeValue = (min, max, input) => {
  if (typeof input !== "number")
    return max - min;
  const minLog = Math.log(min);
  const maxLog = Math.log(max);
  const scale = (maxLog - minLog) / 100;
  const calc = (value) => (Math.log(Number(value)) - minLog) / scale;
  return Number(calc(input).toFixed(2));
};
const exponentiateValue = (min, max, input) => {
  if (typeof input !== "number")
    return max - min;
  const minLog = Math.log(min);
  const maxLog = Math.log(max);
  const scale = (maxLog - minLog) / 100;
  const calc = (value) => Math.floor(Math.exp(Number(value) * scale + minLog));
  return Math.max(min, Math.min(max, calc(input)));
};
const localizeValues = (input, min, max) => input.map((v) => localizeValue(min, max, v));
const exponentiateValues = (input, min, max) => input.map((v) => exponentiateValue(min, max, v));
const Slider = forwardRef((props, ref) => {
  const {
    min = 1,
    max = 100,
    step = 0.1,
    value,
    defaultValue = [max / 2],
    tooltip = (v) => String(v),
    tooltips = "never",
    onChange,
    onCommit,
    onSubmit,
    children,
    classNames,
    uncontrolled = !onChange && !onCommit,
    exponentialScaling
  } = props;
  const [suspended, setSuspended] = useState(false);
  const [localValue, setLocalValue] = useState(() => {
    if (exponentialScaling) {
      return localizeValues(value ?? defaultValue, min, max);
    }
    return value ?? defaultValue;
  });
  const onValueChange = (payload) => {
    setSuspended(true);
    setLocalValue(payload);
    if (exponentialScaling) {
      onChange == null ? void 0 : onChange(exponentiateValues(payload, min, max));
    } else {
      onChange == null ? void 0 : onChange(payload);
    }
  };
  const onValueCommit = (payload) => {
    setSuspended(false);
    if (exponentialScaling) {
      onCommit == null ? void 0 : onCommit(exponentiateValues(payload, min, max));
    } else {
      onCommit == null ? void 0 : onCommit(payload);
    }
  };
  useEffect(() => {
    if (value) {
      if (!uncontrolled || !suspended) {
        if (exponentialScaling) {
          setLocalValue(localizeValues(value, min, max));
        } else {
          setLocalValue(value ?? []);
        }
      }
    }
  }, [value, suspended, uncontrolled, exponentialScaling, min, max]);
  return /* @__PURE__ */ jsxs("form", { onSubmit, children: [
    /* @__PURE__ */ jsxs(
      Radix.Root,
      {
        ref,
        value: localValue,
        min: exponentialScaling ? 0 : min,
        max: exponentialScaling ? 100 : max,
        step: exponentialScaling ? 0.1 : step,
        minStepsBetweenThumbs: 1,
        onValueChange,
        onValueCommit,
        className: cn("relative z-10 flex w-full touch-none select-none items-center", classNames == null ? void 0 : classNames.root),
        children: [
          /* @__PURE__ */ jsx(Radix.Track, { className: "relative flex-1 cursor-pointer py-2", children: /* @__PURE__ */ jsx("div", { className: cn("relative h-px w-full overflow-hidden bg-gray-300", classNames == null ? void 0 : classNames.track), children: /* @__PURE__ */ jsx(Radix.Range, { className: cn("absolute h-full bg-black", classNames == null ? void 0 : classNames.range) }) }) }),
          localValue.map((_, i) => /* @__PURE__ */ jsxs(Radix.Thumb, { className: cn("relative block", classNames == null ? void 0 : classNames.thumb), children: [
            /* @__PURE__ */ jsx("div", { className: "h-3 w-3 bg-black shadow-sm" }),
            tooltips !== "never" ? /* @__PURE__ */ jsx(Tooltip.Provider, { skipDelayDuration: 300, delayDuration: 300, children: /* @__PURE__ */ jsxs(
              Tooltip.Root,
              {
                ...tooltips === "always" && {
                  open: true,
                  onOpenChange: () => null
                },
                children: [
                  /* @__PURE__ */ jsx(Tooltip.Trigger, { asChild: true, children: /* @__PURE__ */ jsx("div", { className: "absolute -left-2 -top-2 h-7 w-7 cursor-grab" }) }),
                  /* @__PURE__ */ jsx(Tooltip.Portal, { children: /* @__PURE__ */ jsxs(
                    Tooltip.Content,
                    {
                      side: "top",
                      onPointerDownOutside: (e) => e.preventDefault(),
                      sideOffset: -4,
                      className: cn("text-xxs bg-black/90 px-2 py-1 text-white shadow", classNames == null ? void 0 : classNames.tooltip),
                      children: [
                        tooltip(exponentialScaling ? exponentiateValue(min, max, localValue.at(i)) : localValue.at(i)),
                        /* @__PURE__ */ jsx(Tooltip.Arrow, { className: "fill-black/90" })
                      ]
                    }
                  ) })
                ]
              }
            ) }) : /* @__PURE__ */ jsx("div", { className: "absolute -left-2 -top-2 h-7 w-7 cursor-grab" })
          ] }, i))
        ]
      }
    ),
    children
  ] });
});
function CartItem(props) {
  const {
    onRemove,
    title,
    brand,
    ActionComponent = null,
    dictionary,
    classNames,
    ImageComponent,
    TitleComponent,
    StockStatusText
  } = props;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn([
        "border-gray-150 grid h-[104px] grid-cols-[103px,_minmax(0,1fr)] items-start border-b",
        classNames == null ? void 0 : classNames.wrapper
      ]),
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn([
              "border-grey-150 bg-grey-100 flex aspect-square w-full items-center justify-center border-r",
              classNames == null ? void 0 : classNames.imageWrapper
            ]),
            children: ImageComponent
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: cn(["mx-3 mb-3 pt-2", classNames == null ? void 0 : classNames.textWrapper]), children: [
          onRemove ? /* @__PURE__ */ jsxs("div", { className: "child:text-xxs child:uppercase mb-[1px] flex items-center justify-between", children: [
            /* @__PURE__ */ jsx("p", { className: "max-w-36 sm:max-w-72 truncate", children: brand }),
            /* @__PURE__ */ jsx(
              Button,
              {
                type: "button",
                className: "min-w-10 w-fit gap-0 self-start border-0 bg-inherit p-0 text-black underline",
                onClick: onRemove,
                children: dictionary == null ? void 0 : dictionary.remove
              }
            )
          ] }) : /* @__PURE__ */ jsx("p", { className: "text-xxs mb-1 truncate text-left", children: brand }),
          TitleComponent ?? /* @__PURE__ */ jsx("div", { className: cn(["flex text-sm", classNames == null ? void 0 : classNames.titleWrapper]), children: /* @__PURE__ */ jsx("p", { className: cn(["truncate text-left", classNames == null ? void 0 : classNames.itemTitle]), children: title }) }),
          StockStatusText && StockStatusText,
          ActionComponent
        ] })
      ]
    }
  );
}
function ProductCard(props) {
  const {
    Campaign,
    limitedEdition,
    PrimaryImage,
    secondaryImage,
    TopComponent,
    dense,
    PreviewImage,
    title,
    onProductClick,
    moreProductVariants,
    secondaryImageHasLoaded,
    stockStatusText,
    brand,
    campaignPrice,
    price
  } = props;
  return /* @__PURE__ */ jsx("article", { className: cn$1("isolate"), children: /* @__PURE__ */ jsxs(
    "div",
    {
      role: "button",
      tabIndex: 0,
      onClick: onProductClick,
      onKeyDown: (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onProductClick();
        }
      },
      children: [
        /* @__PURE__ */ jsxs("div", { className: cn$1("aspect-portrait bg-grey-100 group relative flex overflow-hidden"), children: [
          TopComponent || null,
          Campaign || null,
          moreProductVariants && /* @__PURE__ */ jsx(
            "p",
            {
              className: cn$1(
                "text-xxs absolute bottom-3 right-4 z-10 uppercase leading-none",
                secondaryImage && "transition-opacity group-hover:opacity-0"
              ),
              children: moreProductVariants
            }
          ),
          PreviewImage && PreviewImage,
          PrimaryImage && /* @__PURE__ */ jsx(
            "div",
            {
              className: cn$1(
                "bg-grey-100 absolute inset-0 transition-opacity",
                secondaryImageHasLoaded && "group-hover:opacity-0"
              ),
              children: PrimaryImage
            }
          ),
          limitedEdition && /* @__PURE__ */ jsx("p", { className: "bg-primary text-xxs absolute inset-x-0 bottom-0 text-center uppercase text-black", children: limitedEdition })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: cn$1(dense ? "px-3 pb-3.5 pt-2" : "flex flex-col gap-y-1 px-4 pb-5 pt-3"), children: [
          brand && /* @__PURE__ */ jsx("p", { className: cn$1("text-xxs mb-2 truncate uppercase leading-none"), children: brand }),
          /* @__PURE__ */ jsx("p", { className: cn$1("truncate leading-none", dense && "text-xs"), children: title }),
          /* @__PURE__ */ jsxs("div", { className: cn$1("flex gap-x-2"), children: [
            campaignPrice && /* @__PURE__ */ jsx("p", { children: campaignPrice }),
            /* @__PURE__ */ jsx("p", { className: cn$1(campaignPrice && "text-grey-300 line-through"), children: price })
          ] }),
          stockStatusText && /* @__PURE__ */ jsx("p", { className: "text-xxs uppercase leading-relaxed", children: stockStatusText })
        ] })
      ]
    }
  ) });
}
export {
  Cart,
  CartItem,
  ProductCard,
  Slider,
  exponentiateValue,
  exponentiateValues,
  localizeValue,
  localizeValues
};
